import { FrontSide, DoubleSide } from "three";
import {
  type DesignMaterialParams,
  type LayerParams,
  type LayerConfigParams,
  type MaskParams,
  type SdfCommonParams,
  type SdfDistortion,
  type SdfParams,
  type SdfPattern,
  type SdfRandomTilesParams,
  DESIGN_MATERIAL_VERSION,
} from "./designMaterialTypes";

export const NUM_LAYERS = 4; // There are 4 layers and 4 masks.

export const DEFAULT_TEXTURE_ANISOTROPY = 16; // Default anisotropy for anisotropic textures. Three.js will automatically limit this to the supported anisotropy level.

// Note that increasing this value would also require adding more manually defined random seeds for SDF patterns
// We also have special handling of masking more than 8 SDF patterns in the design material shader.
export const MAX_SDF_PATTERNS = 12;

// A smaller number will result in continuous hatch patterns being split into smaller segments.
// A larger number is not necessarily universally better, since long lines may need to be split
// into smaller segments anyway when we're cutting out windows for example.
export const CONTINUOUS_HATCH_PATTERN_PERIOD = 200;

export const defaultLayerParams: LayerParams = Object.freeze({
  enabled: false,
  srcID: "texture",
  uvTransformID: "layer",
  textureName: "DV_Grunge_Spots_0",
  blendMode: "alpha",
  strength: 1,
  invertInput: false,
  colorCount: 2,
  color0: "#000000",
  color1: "#ffffff",
  color2: "#555555",
  color3: "#aaaaaa",
  useBaseColorAsColor0: false,
  useBaseColorAsColor1: false,
  useBaseColorAsColor2: false,
  useBaseColorAsColor3: false,
  color0Pos: 0.0,
  color1Pos: 1.0,
  color2Pos: 1.0,
  color3Pos: 1.0,
  scaleU: 16,
  scaleV: 16,
  translateU: 0,
  translateV: 0,
  rotate: 0, // in degrees
  maskID: "none",
  maskUvTransformID: "mask",
  sdfMaskRandomness: 0.0,
  sdfMaskRandomAverage: 0.5,
  sdfMaskPatterns: (1 << MAX_SDF_PATTERNS) - 1,
  sdfMaskDepthOverlay: 0.0,
  enableMaskThreshold: false,
  maskThreshold: 0.5,
  invertMask: false,
  colorInfluence: 1.0,
  roughnessInfluence: 0.0,
  metalnessInfluence: 0.0,
  useForHeight: false,
  heightInfluence: 0.2,
  randomUvOffsetStrength: 0.0,
  randomMaskUniqueness: 0.0,
  maskEdgeWearOverlay: 0.0,
});

export const defaultMaskParams: MaskParams = Object.freeze({
  textureName: "DV_Grunge_Spots_0",
  scaleU: 50,
  scaleV: 50,
  translateU: 0,
  translateV: 0,
  rotate: 0, // in degrees
  strength: 0.75,
  contrast: 0,
});

export const defaultSdfPattern: SdfPattern = Object.freeze({
  enabled: false,
  slope: 0, //in degrees
  rotation: 0, //in degrees
  cutXfromY: false,
  cutXfromYScale: 0,
  varyHeight: false,
  varyHeightScale: 0,
  xSize: 5,
  ySize: 1,
  zSize: 1.5,
  xOffset: 0,
  yOffset: 0,
  xDistance: 10,
  yDistance: 10,
  continuousX: false,
  continuousY: false,
  useBevels: false,
  bevelDistanceX: 0.0,
  bevelDistanceY: 0.0,
  bevelDistanceZ: 0.0,
  roundSidesMode: "none",
  roundStrength: 0.05,
  maskRandomSeedIndex: 0,
});

export const defaultSdfCommonParams: SdfCommonParams = Object.freeze({
  maskOnly: true,
  autoGenerateHatchIfMaskOnly: false,
  useShadows: false,
  useAO: false,
  randomMaskSeed: 1.0,
  depth: 1.0,
  aoStrength: 1,
  aoTolerance: 0.5,
  edgeWearStrength: 1.0,
  edgeWearSpread: 1.0,

  thumbnailAoStrength: 1,
  thumbnailAoTolerance: 0.5,

  slopeAxis: "X",

  randomTilesMode: "None",

  rotateAllSdfPatterns: 0,
});

export const defaultSdfRandomTilesParams: SdfRandomTilesParams = Object.freeze({
  tileVariability: 0.5,
  minTileVariability: 0.0,
  tileSplitDirection: "None",
  tileSplitProbability: 0.0,

  alwaysSplitTilesAboveWidth: 100.0,
  alwaysSplitTilesAboveHeight: 100.0,
  minTileHeight: 0.0,
  minTileWidth: 0.0,
});

export const defaultSdfParams: SdfParams = Object.freeze({
  common: defaultSdfCommonParams,

  randomTiles: defaultSdfRandomTilesParams,

  distortions: [],
  patterns: [],
});

export const defaultSdfDistortion: SdfDistortion = Object.freeze({
  enabled: false,
  periodX: 5,
  periodY: 5,
  strengthX: 2,
  strengthY: 2,
  strengthZ: 0.5,
  randomness: 0,
});

export const defaultDesignMaterialParams: DesignMaterialParams = Object.freeze({
  version: DESIGN_MATERIAL_VERSION,
  type: "material",

  allowUsingTemplateOnItsOwn: true,
  compatibleLayerConfigLibraries: [],
  compatibleColorLibraries: [],

  rotateAllLayers: 0,

  color: "#808080",
  transparency: "opaque",
  opacity: 1.0,
  useEnvMap: false,
  envMapIntensity: 0.5,
  roughness: 1.0,
  metalness: 0.0,
  reflectivity: 0.5,
  useSpecularColor: false,
  specularColor: "#101010",
  clearcoat: 0.0,
  clearcoatRoughness: 1.0,
  transmission: 0.0,

  useLegacyGamma: false,

  side: FrontSide,
  shadowSide: DoubleSide,
  layerHeightFilterWidth: 0.7,
  layerHeightAoStrength: 0.5,
  sdf: defaultSdfParams,
  layers: [],
  masks: [],
  normalMapEnabled: true,
  normalMapName: "",
  normalMapStrength: 1.0,
  normalMapScaleU: 50,
  normalMapScaleV: 50,
  normalMapTranslateU: 0,
  normalMapTranslateV: 0,
  normalMapRotation: 0,
  randomizeShader: false,
  randomizeShaderSeed: 0,
  disableSSAO: false,
  mainMaskTexName: "",
  heightMapEnabled: true,
  heightMapName: "",
  heightMapScaleU: 50,
  heightMapScaleV: 50,
  heightMapDepth: 1,
  heightMapRotation: 0,
  hatchMaterialId: "Auto",
  hatchMaterialIgnoreVaryHeight: true,
  hatchMaterialIgnoreBevels: false,
  thumbnailDiagonalCamera: false,
});

export const defaultLayerConfigParams: LayerConfigParams = Object.freeze({
  version: DESIGN_MATERIAL_VERSION,
  type: "layerConfig",
  color: "#808080",
  useLegacyGamma: false,
  layers: [],
  masks: [],
});
